/**
 * Summons — the system the drone class was waiting on (issue #16, docs/04 §3.2).
 *
 * The drone weapon's own blurb has always read "they are entities; they can be
 * shot down", and until now it fired an ordinary long-lived projectile. That is
 * not a small gap in fidelity, it is the whole class: a bullet cannot be
 * targeted, cannot be lost, and cannot be a thing you spend the fight
 * protecting. So the deferral in the doc was correct — the missing piece was a
 * system, not a behaviour.
 *
 * Three decisions shape it:
 *
 *   - **A drone is an ECS entity with `Comp.HEALTH`, not a new kind of object.**
 *     That single choice is what makes it destructible for free: enemy melee
 *     (`enemyAttacks.strike`) and enemy rounds (`Projectiles.hitEntities`) both
 *     already sweep every hostile-team body carrying HEALTH, so nothing had to
 *     learn what a drone is in order to shoot one down. A bespoke drone list
 *     with its own hp field would have needed both of those to grow a special
 *     case, and would have drifted from the damage model docs/03 §6 forbids
 *     splitting.
 *
 *   - **It is not `Comp.ENEMY` and not `Comp.PLAYER`.** The spawner counts,
 *     culls and caps `Comp.ENEMY`; the death path branches on `Comp.PLAYER`. A
 *     drone must be invisible to both — it is neither population nor protagonist
 *     — and the mask is the only place that can be said once.
 *
 *   - **It shoots through `fireFlat`, the same pool as everything else.** A
 *     drone with its own bullet would be the second implementation of a bullet
 *     that `projectiles.ts` opens by refusing.
 *
 * Fixed capacity, dense packing, no allocation after construction — the same
 * contract as `projectiles.ts` and `sim/particles.ts`.
 */

import { BodyFlag, Comp, Team, World } from '../entity/ecs';
import type { DamageTypeId, Entity } from '../entity/ecs';
import { NULL_ENTITY } from '../entity/ecs';
import { M, VOID, Category } from '../sim/materials';
import { MAT } from '../sim/materialData';
import type { CellWorld } from '../sim/world';
import { Projectiles, fireFlat } from './projectiles';
import type { SummonEvent } from './wielder';

/**
 * Hard ceiling on live summons across all owners. The frame budget, not the
 * fiction — each one runs a nearest-hostile scan per tick.
 */
const CAPACITY = 32;
/**
 * Drones one wielder may hold at once. Past this the oldest is retired rather
 * than the newest refused: a summon button that silently does nothing reads as
 * a dropped input, and the magazine is already the rate limit.
 */
const MAX_PER_OWNER = 4;
/**
 * How close a drone closes before it stops advancing and starts shooting. It is
 * a gun platform, not a charger — flying into contact would make it a slow
 * projectile again, which is the shape this system exists to leave behind.
 */
const STANDOFF = 46;
/** Leash: past this from its owner a drone breaks off and comes home. */
const LEASH = 220;
/** Seconds of grace before a fresh drone may fire, so a deploy is not a shot. */
const DEPLOY_DELAY_MS = 220;

export interface DroneSpec {
  hp: number;
  /** Seconds before it powers down on its own. */
  lifetimeSec: number;
  /** Units per second. */
  speed: number;
  /** How far it looks for something to shoot. */
  sight: number;
  fireIntervalMs: number;
  /** Flat damage per round. Drones do not re-resolve the weapon's module chain. */
  damage: number;
  damageType: DamageTypeId;
  /** Half-extent of the body, units. */
  radius: number;
  /** Speed of the rounds it fires. */
  shotSpeed: number;
}

export class Summons {
  /** Generational handles, so a drone destroyed elsewhere is detected, not chased. */
  private handle = new Int32Array(CAPACITY).fill(NULL_ENTITY);
  private ownerIdx = new Int32Array(CAPACITY).fill(-1);
  private life = new Float32Array(CAPACITY);
  private fireTimer = new Float32Array(CAPACITY);
  private specs: Array<DroneSpec | null> = new Array(CAPACITY).fill(null);

  /** Dense packing: [0, count) is live. Removal swaps with the last. */
  count = 0;

  /** Entity index of live summon `k`. Reading order is not stable across deaths. */
  indexAt(k: number): number {
    return this.handle[k] & 0xfffff;
  }

  ownedBy(owner: number): number {
    let n = 0;
    for (let k = 0; k < this.count; k++) if (this.ownerIdx[k] === owner) n++;
    return n;
  }

  /**
   * Put one drone in the world. Returns its entity index, or -1 when the pool
   * or the entity table is full.
   */
  deploy(
    w: World,
    owner: number,
    spec: DroneSpec,
    x: number,
    y: number,
  ): number {
    if (this.ownedBy(owner) >= MAX_PER_OWNER) this.retireOldest(w, owner);
    if (this.count >= CAPACITY) this.retireOldest(w, -1);
    if (this.count >= CAPACITY) return -1;

    let e: Entity;
    try {
      e = w.create(Comp.TRANSFORM | Comp.SPRITE | Comp.HEALTH);
    } catch {
      return -1; // capacity exhausted; not fatal
    }
    const i = e & 0xfffff;
    w.x[i] = x;
    w.y[i] = y;
    w.vx[i] = 0;
    w.vy[i] = 0;
    w.halfW[i] = spec.radius;
    w.halfH[i] = spec.radius;
    w.hp[i] = w.maxHp[i] = Math.max(1, spec.hp);
    // A drone is its owner's, so the player cannot shoot their own flock and
    // enemies can (`Projectiles.hitEntities` and `strike` both filter on team).
    w.team[i] = w.team[owner];
    w.owner[i] = w.handleAt(owner);
    // It hovers, and it has no balance to lose: `maxPoise` of 0 is what keeps
    // the shared stagger rule in `enemyAttacks.strike` from applying to a thing
    // with no feet.
    w.gravityScale[i] = 0;
    w.bodyFlags[i] = BodyFlag.NO_GRAVITY | BodyFlag.NO_STEP_UP;
    w.maxPoise[i] = w.poise[i] = 0;
    w.facing[i] = w.facing[owner] || 1;
    // `Game.drawEntities` colours a body by what it is made of (docs/03 §6), so
    // an unset `coreMat` would render the flock as VOID-black rectangles — the
    // one thing on screen that is not the colour of its own material.
    w.coreMat[i] = MAT.BRASS;

    const k = this.count++;
    this.handle[k] = e;
    this.ownerIdx[k] = owner;
    this.life[k] = spec.lifetimeSec;
    this.fireTimer[k] = DEPLOY_DELAY_MS / 1000;
    this.specs[k] = spec;
    return i;
  }

  /**
   * Fly, pick targets, shoot, and reap.
   *
   * Reaping covers three doors, and all three have to be one place or a drone
   * leaks: its own lifetime ran out, something killed it (`hp <= 0`), or the
   * entity was destroyed from outside and the handle no longer resolves.
   */
  step(w: World, cells: CellWorld, proj: Projectiles, dt: number): void {
    if (dt <= 0) return;
    for (let k = 0; k < this.count; k++) {
      const h = this.handle[k];
      const i = h & 0xfffff;

      if (!w.valid(h) || w.hp[i] <= 0) {
        this.kill(w, k);
        k--;
        continue;
      }
      this.life[k] -= dt;
      if (this.life[k] <= 0) {
        this.kill(w, k);
        k--;
        continue;
      }

      const spec = this.specs[k]!;
      const owner = this.ownerIdx[k];
      const ownerAlive = owner >= 0 && w.alive[owner] === 1;
      const target = this.pickTarget(w, i, spec.sight);

      // Home when there is nothing to shoot, or when the leash is out. The
      // leash is checked first: a drone that chased a target across the room
      // and stayed there is a drone the player has lost without being told.
      let gx = w.x[i];
      let gy = w.y[i];
      let holding = true;
      const homeD = ownerAlive ? Math.hypot(w.x[owner] - w.x[i], w.y[owner] - w.y[i]) : 0;
      if (ownerAlive && homeD > LEASH) {
        gx = w.x[owner];
        gy = w.y[owner];
        holding = false;
      } else if (target >= 0) {
        const d = Math.hypot(w.x[target] - w.x[i], w.y[target] - w.y[i]);
        if (d > STANDOFF) {
          gx = w.x[target];
          gy = w.y[target];
          holding = false;
        }
      } else if (ownerAlive && homeD > spec.radius * 6) {
        // Idle station-keeping: just above and behind the wielder's head, so a
        // deployed flock is legible rather than clipping through them.
        gx = w.x[owner];
        gy = w.y[owner] - 18;
        holding = false;
      }

      if (!holding) this.fly(w, cells, i, gx, gy, spec.speed, dt);
      else {
        w.vx[i] = 0;
        w.vy[i] = 0;
      }

      this.fireTimer[k] -= dt;
      if (target >= 0 && this.fireTimer[k] <= 0) {
        const dx = w.x[target] - w.x[i];
        const dy = w.y[target] - w.y[i];
        const dl = Math.hypot(dx, dy) || 1;
        w.facing[i] = dx >= 0 ? 1 : -1;
        fireFlat(
          proj, w, i,
          w.x[i] + (dx / dl) * (spec.radius + 2),
          w.y[i] + (dy / dl) * (spec.radius + 2),
          dx / dl, dy / dl,
          spec.shotSpeed,
          spec.damage,
          spec.damageType,
          // Range and speed imply flight time, the same derivation
          // `enemyAttacks.spawnProjectile` makes, so a third number cannot
          // disagree with the other two.
          Math.max(0.4, spec.sight / Math.max(60, spec.shotSpeed)) + 0.2,
          0,
          2,
        );
        this.fireTimer[k] = spec.fireIntervalMs / 1000;
      }
    }
  }

  /**
   * Move toward a goal, per axis, refusing to enter rigid structure.
   *
   * Per-axis rather than as a vector so a drone brushing a wall slides along it
   * instead of parking against it — `RIGID` only, matching the rule
   * `projectiles.ts` settled on: loose matter is not a barrier to something
   * this small, and treating it as one would strand a flock in a sandfall.
   */
  private fly(
    w: World,
    cells: CellWorld,
    i: number,
    gx: number,
    gy: number,
    speed: number,
    dt: number,
  ): void {
    const dx = gx - w.x[i];
    const dy = gy - w.y[i];
    const dl = Math.hypot(dx, dy) || 1;
    const step = Math.min(dl, speed * dt);
    const nx = w.x[i] + (dx / dl) * step;
    const ny = w.y[i] + (dy / dl) * step;
    if (!blocked(cells, nx, w.y[i])) w.x[i] = nx;
    if (!blocked(cells, w.x[i], ny)) w.y[i] = ny;
    w.vx[i] = (dx / dl) * speed;
    w.vy[i] = (dy / dl) * speed;
  }

  /** Nearest hostile body with health, inside `sight`. */
  private pickTarget(w: World, i: number, sight: number): number {
    const team = w.team[i];
    let best = -1;
    let bestD2 = sight * sight;
    w.each(Comp.HEALTH, (j) => {
      if (j === i || w.hp[j] <= 0) return;
      if (w.team[j] === team || w.team[j] === Team.NEUTRAL) return;
      const dx = w.x[j] - w.x[i];
      const dy = w.y[j] - w.y[i];
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = j;
      }
    });
    return best;
  }

  private retireOldest(w: World, owner: number): void {
    for (let k = 0; k < this.count; k++) {
      if (owner >= 0 && this.ownerIdx[k] !== owner) continue;
      this.kill(w, k);
      return;
    }
  }

  private kill(w: World, k: number): void {
    const h = this.handle[k];
    if (w.valid(h)) w.destroy(h);
    this.remove(k);
  }

  private remove(k: number): void {
    const last = --this.count;
    if (k !== last) {
      this.handle[k] = this.handle[last];
      this.ownerIdx[k] = this.ownerIdx[last];
      this.life[k] = this.life[last];
      this.fireTimer[k] = this.fireTimer[last];
      this.specs[k] = this.specs[last];
    }
    this.handle[last] = NULL_ENTITY;
    this.ownerIdx[last] = -1;
    this.specs[last] = null;
  }

  clear(w: World): void {
    for (let k = 0; k < this.count; k++) {
      const h = this.handle[k];
      if (w.valid(h)) w.destroy(h);
      this.handle[k] = NULL_ENTITY;
      this.ownerIdx[k] = -1;
      this.specs[k] = null;
    }
    this.count = 0;
  }
}

function blocked(cells: CellWorld, x: number, y: number): boolean {
  const m = cells.peek(Math.floor(x), Math.floor(y));
  return m !== VOID && M.category[m] === Category.RIGID;
}

// ------------------------------------------------------------ fire handler

/**
 * Derive a drone from the weapon that deployed it.
 *
 * Everything here comes off the resolved weapon, so modules reach the flock:
 * `lifetimeMul` extends how long it stays up, `speedMul` how fast it flies, and
 * `countAdd` how many a press puts out. A drone with hardcoded numbers would be
 * the one part of the build that a build cannot touch — the §5 buildcraft space
 * stopping at the summon is exactly the kind of quiet exception that makes a
 * class feel bolted on.
 */
export function droneSpecFor(e: SummonEvent): DroneSpec {
  const wp = e.weapon;
  const f = e.node.fire;
  const p = wp.projectile;
  const dmg = wp.damage * e.node.damageMul * e.charge;
  return {
    // Health scales with the weapon, because a relic-tier flock that dies to
    // the same one hit as a common one is not a better flock.
    hp: 10 + dmg,
    lifetimeSec: Math.max(1, (f?.lifetime ?? 12) * p.lifetimeMul),
    speed: Math.max(40, (f?.speed ?? 180) * p.speedMul),
    sight: Math.max(60, wp.reach),
    // The drone inherits the wielder's rate of fire — a fast summoner is a
    // suppression tool, a slow one is a turret.
    //
    // **`wp.cadence` is not that rate**, and this is the last live consumer
    // still reading it as one. Its base is unitless: `cadenceMulBase` is the
    // reference point of the `cadenceMul` chain and cancels out of its own
    // formula, so `1000 / cadence` is a duration in nothing. #39 named the
    // wielder's real rate — the **opener**, `1000 / nodeTiming(wp,
    // wp.entries.light).total` — and `uiAdapter.ts` and `weaponAltar.ts`, the
    // two *display* readers of `cadence`, now print that instead.
    //
    // This one is deliberately left alone, because it is not a display: it is
    // the flock's actual DPS. `warden_drones` measures
    //
    //     cadence-derived   1250 ms  (0.80/s)   9.6 shots over a 12 s life
    //     opener-derived     131 ms  (7.63/s)  91.6 shots over a 12 s life
    //
    // — a **9.5x** buff to the entire summoner class, arriving as a side
    // effect of a units fix. Whether a drone should fire at the wielder's
    // opener rate, at some fraction of it, or at an interval of its own is a
    // tuning decision that belongs to Phase 7 with the rest of the class
    // balance, not to the patch that renamed the units. Until then the flock
    // keeps the behaviour it shipped with, and the reason it is not what the
    // comment above claims is written down rather than inferred.
    fireIntervalMs: 1000 / Math.max(0.4, wp.cadence),
    // A fraction, not the swing: a drone that dealt the weapon's full damage
    // every 1.25 s for 12 s would be worth more than everything else in the
    // catalogue put together.
    damage: dmg * 0.5,
    damageType: wp.damageType,
    radius: 3,
    shotSpeed: 340,
  };
}

/**
 * The `WielderCtx.onSummon` implementation — the counterpart of
 * `makeFireHandler`, and the whole wiring the game shell installs.
 */
export function makeSummonHandler(
  summons: Summons,
  w: World,
): (e: SummonEvent) => void {
  return (e) => {
    const spec = droneSpecFor(e);
    for (let k = 0; k < e.count; k++) {
      // Fanned along the aim line so a three-drone heavy does not stack three
      // bodies on one pixel.
      const spread = (k - (e.count - 1) / 2) * 8;
      summons.deploy(
        w, e.owner, spec,
        e.x + e.dirX * 4 - e.dirY * spread,
        e.y + e.dirY * 4 + e.dirX * spread,
      );
    }
  };
}
